/**
 * Scroll the list under the cursor while a drag is in progress (#84).
 *
 * A drag holds the pointer captive: the wheel is ignored by most browsers during a native file drag,
 * and there is no keyboard. So a drop target that is off-screen when the drag starts simply cannot be
 * reached — the user has to abort the drag, scroll, and start again. Every list in the app is its own
 * scroll container (the shell locks the document itself), so "scroll the page" is not even available
 * as a fallback.
 *
 * Deliberately global and drag-kind agnostic: it listens on the window in the capture phase and never
 * calls `preventDefault`, so it changes nothing about which zone accepts a drop or what happens on it.
 * It applies to native file drags AND to the app's own `application/x-marcus-item` drags, because the
 * problem is the pointer, not the payload.
 */

/** How close to an edge the pointer must come before the container starts moving. */
const EDGE_PX = 72
/** Pixels per frame at the very edge — about 1300px/s at 60fps, a screen-height every half second. */
const MAX_SPEED_PX = 22
/**
 * A drag is considered over when no `dragover` has arrived for this long.
 *
 * The spec has the browser fire `dragover` continuously while a drag is over the document (roughly
 * every 350ms, even with the pointer stationary), so silence means the drag left the window or ended
 * somewhere we were not told about — a file dropped outside it, or a drag cancelled with Escape.
 * Without this the loop would keep scrolling a list nobody is dragging over.
 */
const IDLE_MS = 600

/**
 * How far to scroll this frame: negative up, positive down, zero when the pointer is nowhere near an
 * edge. Pure, and exported for the test — the geometry is the part worth pinning, and it needs no DOM.
 */
export function autoScrollDelta(box: { top: number; bottom: number }, pointerY: number): number {
  const fromTop = pointerY - box.top
  const fromBottom = box.bottom - pointerY
  // Outside the container entirely (the pointer is over a child that overflows it, say) — do nothing
  // rather than guess a direction.
  if (fromTop < 0 || fromBottom < 0) return 0
  if (fromTop < EDGE_PX) return -ramp(fromTop)
  if (fromBottom < EDGE_PX) return ramp(fromBottom)
  return 0
}

/** Full speed at the edge, easing to nothing at the end of the band — a constant speed overshoots. */
function ramp(distance: number): number {
  return Math.max(1, Math.round(MAX_SPEED_PX * (1 - distance / EDGE_PX)))
}

/** The nearest ancestor of the point that can actually scroll vertically. */
function scrollableUnder(x: number, y: number): HTMLElement | null {
  let el = document.elementFromPoint(x, y) as HTMLElement | null
  while (el) {
    // `+ 1` absorbs the sub-pixel difference a zoomed or fractionally-sized element reports.
    if (el.scrollHeight > el.clientHeight + 1) {
      const overflow = getComputedStyle(el).overflowY
      if (overflow === 'auto' || overflow === 'scroll') return el
    }
    el = el.parentElement
  }
  return null
}

export function useDragAutoScroll() {
  let pointer: { x: number; y: number } | null = null
  let lastMove = 0
  let frame: number | null = null

  function step() {
    frame = null
    if (!pointer) return
    if (Date.now() - lastMove > IDLE_MS) { stop(); return }

    const el = scrollableUnder(pointer.x, pointer.y)
    if (el) {
      const delta = autoScrollDelta(el.getBoundingClientRect(), pointer.y)
      if (delta !== 0) el.scrollTop += delta
    }
    frame = requestAnimationFrame(step)
  }

  function onDragOver(e: DragEvent) {
    pointer = { x: e.clientX, y: e.clientY }
    lastMove = Date.now()
    if (frame === null) frame = requestAnimationFrame(step)
  }

  function stop() {
    pointer = null
    if (frame !== null) {
      cancelAnimationFrame(frame)
      frame = null
    }
  }

  onMounted(() => {
    // Capture phase: a zone that stops propagation on its own `dragover` (the chat panel does) must
    // not switch this off for the list it is sitting on top of.
    window.addEventListener('dragover', onDragOver, { capture: true, passive: true })
    window.addEventListener('drop', stop, true)
    window.addEventListener('dragend', stop, true)
  })

  onUnmounted(() => {
    window.removeEventListener('dragover', onDragOver, true)
    window.removeEventListener('drop', stop, true)
    window.removeEventListener('dragend', stop, true)
    stop()
  })
}
